import type { AppConfig } from '@/config/env';
import type { LogContext, Logger } from './types';

const asError = (value: unknown): Error => (value instanceof Error ? value : new Error(String(value)));

let sentryInitialized = false;

// Per-event chatter: hidden at Chrome's default console level, shown with "Verbose" or ?debug=1
const VERBOSE_MESSAGES = new Set([
  'divkitStat',
  'globalVariableChanged',
  'postPaymentVariableChanged',
  'analyticsEvent',
  'eventEmitted',
  'quizExpressionAnswersResolved',
]);

// Repeated per-key warnings: report each distinct payload once per page load
const DEDUPED_WARNINGS = new Set(['localizationKeyMissing', 'tariffTextMissing']);
const seenWarnings = new Set<string>();

const warningKey = (message: string, data: unknown): string => {
  try {
    return `${message}:${JSON.stringify(data)}`;
  } catch {
    return `${message}:${String(data)}`;
  }
};

export const createLogger = (config: AppConfig): Logger => {
  const verbose = config.query?.debug === true;
  const sentry = config.sentryDsn ? import('@sentry/browser') : Promise.resolve(undefined);

  // The logger is created both in App and on the page — init Sentry only once.
  if (!sentryInitialized) {
    sentryInitialized = true;
    void sentry.then((Sentry) => {
      Sentry?.init({
        dsn: config.sentryDsn,
        environment: config.environment,
        enabled: true,
      });
    });
  }

  return {
    logInfo(data, message) {
      const label = `[OrganicWebFunnel] ${message ?? 'info'}`;
      if (!verbose && message && VERBOSE_MESSAGES.has(message)) {
        console.debug(label, data);
        return;
      }
      console.info(label, data);
    },
    logWarning(data, message) {
      if (!verbose && message && DEDUPED_WARNINGS.has(message)) {
        const key = warningKey(message, data);
        if (seenWarnings.has(key)) return;
        seenWarnings.add(key);
      }
      console.warn(`[OrganicWebFunnel] ${message ?? 'warning'}`, data);
    },
    logError(error, message) {
      console.error(`[OrganicWebFunnel] ${message ?? 'error'}`, error);
    },
    captureException(error, context = {}) {
      const normalizedError = asError(error);
      console.error('[OrganicWebFunnel] captured exception', normalizedError, context);
      if (!config.sentryDsn) {
        return;
      }

      void sentry.then((Sentry) => {
        Sentry?.withScope((scope) => {
          Object.entries(context).forEach(([key, value]) => scope.setExtra(key, value));
          Sentry.captureException(normalizedError);
        });
      });
    },
  };
};

export type { LogContext, Logger };
